import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Calculator, TrendingUp, Save, AlertCircle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api from "../lib/api";
import { PageHeader, Loading, Money } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Pricing() {
  const qc = useQueryClient();
  const { data: catalog, isLoading } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/pricing/catalog")).data });
  const [lines, setLines] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [discount, setDiscount] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [travel, setTravel] = useState(0);
  const [thirdParty, setThirdParty] = useState(0);
  const [result, setResult] = useState(null);
  const [overrideFor, setOverrideFor] = useState(null);
  const [overrideVal, setOverrideVal] = useState({ price: "", reason: "" });

  const addLine = (item) => setLines((l) => [...l, {
    catalog_id: item.id, name: item.name, quantity: 1, base_rate: item.base_rate,
    delivery_cost_rate: item.delivery_cost_rate, complexity_multiplier: 1.0, recurring: item.recurring,
    override_price: null, override_reason: "",
  }]);
  const updateLine = (i, patch) => setLines((l) => l.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const body = useMemo(() => ({
    org_name: orgName, currency: "USD", lines, platform_fee_monthly: Number(platformFee) || 0,
    travel_cost: Number(travel) || 0, third_party_cost: Number(thirdParty) || 0, discount_pct: Number(discount) || 0,
  }), [orgName, lines, platformFee, travel, thirdParty, discount]);

  const calc = useMutation({ mutationFn: () => api.post("/pricing/calculate", body), onSuccess: (r) => setResult(r.data) });
  const saveQuote = useMutation({ mutationFn: () => api.post("/pricing/quotes", body), onSuccess: () => { qc.invalidateQueries({ queryKey: ["quotes"] }); toast.success("Quote saved (advisory — you approve the final quote)"); } });

  useEffect(() => { if (lines.length) calc.mutate(); else setResult(null); /* eslint-disable-next-line */ }, [body]);

  if (isLoading) return <Loading />;

  const projection = result ? Array.from({ length: 12 }, (_, i) => ({ month: `M${i + 1}`, revenue: result.recurring_monthly * (i + 1) })) : [];

  return (
    <div>
      <PageHeader eyebrow="Pricing & Revenue" title="Pricing Calculator"
        description="Build a quote from your service catalogue. See client price, delivery cost, gross margin and recurring revenue. Pricing is advisory — you approve the final quote." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* catalog */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Service catalogue</h3>
          <div className="space-y-2">
            {(catalog || []).map((item) => (
              <button key={item.id} onClick={() => addLine(item)} data-testid={`catalog-add-${item.id}`}
                className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors group">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">{item.name}</span>
                  <Plus className="w-4 h-4 text-slate-300 group-hover:text-emerald-600" />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-400">{item.recurring ? "Recurring" : "One-off"} · {item.category}</span>
                  <span className="text-xs font-semibold text-emerald-700"><Money value={item.base_rate} />/{item.unit}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* builder */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-slate-900">Quote lines</h3>
              <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="quote-org" placeholder="Client name" className="max-w-[200px] h-9" />
            </div>
            {lines.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Add services from the catalogue to build a quote.</p> : (
              <div className="space-y-3">
                {lines.map((ln, i) => (
                  <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`quote-line-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{ln.name} {ln.recurring && <span className="text-[10px] font-mono text-teal-600">/mo</span>}</span>
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div><span className="text-[10px] text-slate-400">Qty</span><Input type="number" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} className="h-8 text-sm" /></div>
                      <div><span className="text-[10px] text-slate-400">Base rate</span><Input type="number" value={ln.base_rate} onChange={(e) => updateLine(i, { base_rate: Number(e.target.value) })} className="h-8 text-sm" /></div>
                      <div><span className="text-[10px] text-slate-400">Complexity ×</span><Input type="number" step="0.1" value={ln.complexity_multiplier} onChange={(e) => updateLine(i, { complexity_multiplier: Number(e.target.value) })} className="h-8 text-sm" /></div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      {ln.override_price != null ? (
                        <span className="text-[11px] text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Override: <Money value={ln.override_price} /> — {ln.override_reason}</span>
                      ) : <span className="text-[11px] text-slate-400">Line price: <Money value={ln.base_rate * ln.quantity * ln.complexity_multiplier} /></span>}
                      <button onClick={() => { setOverrideFor(i); setOverrideVal({ price: ln.override_price ?? "", reason: ln.override_reason ?? "" }); }} className="text-[11px] text-emerald-600 hover:underline" data-testid={`override-${i}`}>Override price</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
              <div><Label className="text-xs">Platform /mo</Label><Input type="number" value={platformFee} onChange={(e) => setPlatformFee(e.target.value)} className="h-8 mt-1" /></div>
              <div><Label className="text-xs">Travel</Label><Input type="number" value={travel} onChange={(e) => setTravel(e.target.value)} className="h-8 mt-1" /></div>
              <div><Label className="text-xs">3rd party</Label><Input type="number" value={thirdParty} onChange={(e) => setThirdParty(e.target.value)} className="h-8 mt-1" /></div>
              <div><Label className="text-xs">Discount %</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-8 mt-1" /></div>
            </div>
          </div>

          {result && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: "Client price", value: result.client_price, color: "text-slate-900" },
                  { label: "Delivery cost", value: result.delivery_cost, color: "text-slate-500" },
                  { label: "Gross margin", value: result.gross_margin, color: "text-emerald-600", sub: `${result.margin_pct}%` },
                  { label: "Recurring /mo", value: result.recurring_monthly, color: "text-teal-600" },
                ].map((m) => (
                  <div key={m.label} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="text-xs text-slate-400">{m.label}</div>
                    <div className={`font-display text-xl font-bold mt-1 ${m.color}`}><Money value={m.value} /></div>
                    {m.sub && <div className="text-xs text-emerald-600 mt-0.5">{m.sub} margin</div>}
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
                <h3 className="font-display font-semibold text-slate-900 mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-teal-600" /> Recurring revenue projection (12 mo)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={projection}>
                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#14b8a6" stopOpacity={0.3} /><stop offset="100%" stopColor="#14b8a6" stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    <Area type="monotone" dataKey="revenue" stroke="#14b8a6" strokeWidth={2} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex justify-end">
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveQuote.mutate()} data-testid="save-quote"><Save className="w-4 h-4 mr-1.5" /> Save quote</Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={overrideFor !== null} onOpenChange={(o) => !o && setOverrideFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Override line price</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Manual overrides require a reason for your records.</p>
          <div><Label>Override price</Label><Input type="number" value={overrideVal.price} onChange={(e) => setOverrideVal({ ...overrideVal, price: e.target.value })} data-testid="override-price" className="mt-1.5" /></div>
          <div><Label>Reason</Label><Input value={overrideVal.reason} onChange={(e) => setOverrideVal({ ...overrideVal, reason: e.target.value })} data-testid="override-reason" className="mt-1.5" placeholder="e.g. strategic client discount" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { updateLine(overrideFor, { override_price: null, override_reason: "" }); setOverrideFor(null); }}>Clear override</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!overrideVal.price || !overrideVal.reason} data-testid="override-confirm"
              onClick={() => { updateLine(overrideFor, { override_price: Number(overrideVal.price), override_reason: overrideVal.reason }); setOverrideFor(null); }}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
